/**
 * PATH: packages/email-hub/src/feature/components/ThreadAttachmentBar.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-THREADATTACHMENTBAR-001
 * PURPOSE: The sticky bottom attachment bar for the stacked-thread reader.
 *          Stays visible while the operator scrolls the conversation and
 *          shows every attachment in the thread, grouped + colour-coded by
 *          the message it arrived on. Clicking a file opens the shared
 *          preview modal; clicking a group's sender label scrolls to that
 *          message.
 * WHY:     Operator request — "attachments should be visible at the bottom
 *          as a fixed item as you scroll … with changing sectionalised
 *          colour codes if they are attached to different sections." Colour
 *          ties each file back to its source message (see threadColors.ts).
 * RELATED:
 *   - components/EmailThread.tsx (builds the groups + flat preview list)
 *   - components/ThreadMessage.tsx (the colour-keyed blocks scrolled to)
 */

import { Paperclip } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { formatFileSize } from '../lib/utils'
import { attachmentChipStyle } from '../lib/attachmentIcons'
import type { ThreadColor } from '../lib/threadColors'
import type { Email, EmailAttachment } from '../hooks/useEmails'

export interface ThreadAttachmentGroup {
  email: Email
  color: ThreadColor
  /** Visible attachments for this message (selectVisibleAttachments order). */
  attachments: EmailAttachment[]
  /** Index of this group's first attachment in the flat thread list — the
   *  shared AttachmentPreviewModal is addressed by this flat index. */
  flatStartIndex: number
}

export interface ThreadAttachmentBarProps {
  groups: ThreadAttachmentGroup[]
  /** Open the shared preview modal at a flat thread-attachment index. */
  onPreview: (flatIndex: number) => void
  /** Scroll the conversation to a message. */
  onJump: (emailId: string) => void
  /** Highlight the group whose message is currently in view (scroll-spy). */
  activeEmailId?: string | null
}

function groupLabel(email: Email): string {
  return (
    email.from_name ||
    (email.from_address ?? '').split('@')[0] ||
    'Unknown'
  )
}

export default function ThreadAttachmentBar({
  groups,
  onPreview,
  onJump,
  activeEmailId,
}: ThreadAttachmentBarProps) {
  const populated = groups.filter((g) => g.attachments.length > 0)
  if (populated.length === 0) return null

  const totalCount = populated.reduce((n, g) => n + g.attachments.length, 0)

  return (
    <div
      data-testid="thread-attachment-bar"
      className={cn(
        'sticky bottom-0 z-20 -mx-3 mt-4 border-t border-border',
        'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
        <span className="inline-flex items-center gap-1 shrink-0 text-[11px] font-medium text-muted-foreground pr-1">
          <Paperclip className="h-3.5 w-3.5" />
          {totalCount}
        </span>

        {populated.map((g) => {
          const isActive = activeEmailId === g.email.id
          return (
            <div
              key={g.email.id}
              className={cn(
                'flex items-center gap-1 shrink-0 rounded-lg px-1.5 py-0.5 border border-transparent',
                g.color.chipBg,
                isActive && cn('ring-1', g.color.ring),
              )}
            >
              {/* Group label — scroll to the source message */}
              <button
                type="button"
                onClick={() => onJump(g.email.id)}
                title={`Jump to message from ${groupLabel(g.email)}`}
                className="inline-flex items-center gap-1 shrink-0 max-w-[120px]"
              >
                <span className={cn('h-2 w-2 rounded-full shrink-0', g.color.dot)} />
                <span className={cn('text-[11px] font-medium truncate', g.color.text)}>
                  {groupLabel(g.email)}
                </span>
              </button>

              {/* Files in this group — compact chips. A small TINTED,
                  colour-coded type badge (PDF rose, image sky, doc blue…)
                  makes the doc type readable at a glance; the filename is
                  tightly truncated to keep the bar dense. Full name + size
                  on hover. Message-colour stays on the group dot above. */}
              <div className="flex items-center gap-1">
                {g.attachments.map((att, idx) => {
                  const fileStyle = attachmentChipStyle(att)
                  const FileIcon = fileStyle.icon
                  return (
                    <button
                      key={att.id}
                      type="button"
                      onClick={() => onPreview(g.flatStartIndex + idx)}
                      title={`${att.filename}${att.size ? ` · ${formatFileSize(att.size)}` : ''}`}
                      className={cn(
                        'inline-flex items-center gap-1 shrink-0 max-w-[132px]',
                        'rounded-md px-1 py-0.5',
                        'text-[11px] text-foreground hover:bg-accent transition-colors',
                      )}
                    >
                      <span
                        className={cn(
                          'inline-flex h-4 w-4 items-center justify-center rounded shrink-0',
                          fileStyle.tintClass,
                        )}
                      >
                        <FileIcon className={cn('h-3 w-3', fileStyle.iconClass)} />
                      </span>
                      <span className="truncate">{att.filename}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
